/**
 * TSOG 閲覧履歴 - Splide初期化と履歴管理
 */

class TsogRecentlyViewed {
  constructor() {
    this.storageKey = 'tsog_recently_viewed_products';
    this.init();
  }

  init() {
    // 現在の商品を履歴に追加（商品ページの場合）
    this.saveCurrentProduct();

    // DOMContentLoadedまたはSplide読み込み完了を待つ
    if (typeof Splide !== 'undefined') {
      this.initializeSections();
    } else {
      const checkSplide = setInterval(() => {
        if (typeof Splide !== 'undefined') {
          clearInterval(checkSplide);
          this.initializeSections();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkSplide);
        console.warn('Splide library failed to load for recently viewed');
      }, 10000);
    }
  }

  // 現在の商品を履歴に保存
  saveCurrentProduct() {
    const sections = document.querySelectorAll('[data-current-product-handle]');
    if (sections.length === 0) return;

    const productHandle = sections[0].dataset.currentProductHandle;
    if (!productHandle) return;

    let history = this.getHistory();

    // 現在の商品を履歴から削除（重複防止）
    history = history.filter(handle => handle !== productHandle);

    // 先頭に追加
    history.unshift(productHandle);

    // 最大20件まで保存
    history = history.slice(0, 20);

    this.saveHistory(history);
  }

  // 履歴を取得
  getHistory() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to get recently viewed history:', e);
      return [];
    }
  }

  // 履歴を保存
  saveHistory(history) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(history));
    } catch (e) {
      console.error('Failed to save recently viewed history:', e);
    }
  }

  // セクションを初期化
  initializeSections() {
    if (!window.tsogRecentlyViewedSections || window.tsogRecentlyViewedSections.length === 0) {
      return;
    }

    window.tsogRecentlyViewedSections.forEach((config) => {
      this.initializeSection(config);
    });

    // 初期化後にリセット
    window.tsogRecentlyViewedSections = [];
  }

  // 個別セクションを初期化
  async initializeSection(config) {
    const {
      sectionId,
      perPage = 4,
      perPageMobile = 2,
      maxProducts = 12,
      currentProductHandle,
      showVendor = false,
      showRating = false,
      imageRatio = 'adapt',
      imageShape = 'default',
      showSecondaryImage = false
    } = config;

    const sliderElement = document.querySelector(`.tsog-recently-viewed-splide-${sectionId}`);
    const progressElement = document.querySelector(`.tsog-recently-viewed-progress`);
    const emptyElement = document.querySelector(`.tsog-recently-viewed__empty`);

    if (!sliderElement) return;

    // 履歴を取得（現在の商品を除外）
    let history = this.getHistory().filter(handle => handle !== currentProductHandle);
    history = history.slice(0, maxProducts);

    if (history.length === 0) {
      // 履歴がない場合
      if (emptyElement) emptyElement.style.display = 'block';
      return;
    }

    // 商品データを取得
    const products = await this.fetchProducts(history);

    if (products.length === 0) {
      if (emptyElement) emptyElement.style.display = 'block';
      return;
    }

    // 商品カードをレンダリング
    const listElement = sliderElement.querySelector('.splide__list');
    products.forEach(product => {
      const li = this.createProductCard(product, {
        showVendor,
        showRating,
        imageRatio,
        imageShape,
        showSecondaryImage
      });
      listElement.appendChild(li);
    });

    // カートに追加ボタンの動作をバインド
    this.bindCartButtons(listElement);

    // スライダーとプログレスバーを表示
    sliderElement.style.display = 'block';
    if (progressElement) progressElement.style.display = 'block';

    // Splideを初期化
    this.initializeSlider(sectionId, perPage, products.length, perPageMobile);
  }

  // 商品データを取得
  async fetchProducts(productHandles) {
    const products = [];

    for (const handle of productHandles) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          products.push(product);
        }
      } catch (e) {
        console.error(`Failed to fetch product ${handle}:`, e);
      }
    }

    return products;
  }

  // 商品カードを作成
  createProductCard(product, options) {
    const li = document.createElement('li');
    li.className = 'splide__slide tsog-recently-viewed__item';

    const {
      showVendor,
      showRating,
      imageRatio,
      imageShape,
      showSecondaryImage
    } = options;

    // 画像
    const imageUrl = product.featured_image || (product.images && product.images[0]) || '';
    const secondaryImage = showSecondaryImage && product.images && product.images[1] ? product.images[1] : '';

    // 価格（Shopify Ajax APIは cents単位で返す）
    const price = product.price ? (product.price / 100).toLocaleString('ja-JP') : '0';
    const comparePrice = product.compare_at_price && product.compare_at_price > product.price
      ? (product.compare_at_price / 100).toLocaleString('ja-JP')
      : null;

    // スウォッチは非表示（関連商品と統一）。表示したい場合は this.buildColorSwatches(product) に戻す
    const swatchesHtml = '';
    const cartHtml = this.buildCartButton(product);

    li.innerHTML = `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--standard card--media" style="--ratio-percent: 100%;">
          <div class="card__inner ratio" style="--ratio-percent: 100%;">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect media--${imageRatio}">
                <a href="${product.url || '#'}" class="tsog-rv__image-link" tabindex="-1">
                ${imageUrl ? `
                  <img
                    srcset="${imageUrl}&width=165 165w, ${imageUrl}&width=360 360w, ${imageUrl}&width=533 533w, ${imageUrl}&width=720 720w, ${imageUrl}&width=940 940w"
                    src="${imageUrl}&width=533"
                    sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                    alt="${product.title || ''}"
                    class="motion-reduce"
                    loading="lazy"
                    width="533"
                    height="533"
                  >
                  ${secondaryImage ? `
                    <img
                      srcset="${secondaryImage}&width=360 360w, ${secondaryImage}&width=533 533w, ${secondaryImage}&width=720 720w"
                      src="${secondaryImage}&width=533"
                      sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                      alt="${product.title || ''}"
                      class="motion-reduce media--hover-effect__image"
                      loading="lazy"
                      width="533"
                      height="533"
                    >
                  ` : ''}
                ` : `<div class="placeholder-svg"></div>`}
                </a>
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="${product.url || '#'}" class="full-unstyled-link">${product.title || ''}</a>
              </h3>
              <div class="card-information">
                ${showVendor && product.vendor ? `<div class="caption-with-letter-spacing light">${product.vendor}</div>` : ''}
                ${swatchesHtml}
                <div class="price">
                  <div class="price__container">
                    ${comparePrice ? `
                      <div class="price__sale">
                        <span class="price-item price-item--regular"><s>¥${comparePrice}</s></span>
                        <span class="price-item price-item--sale price-item--last">¥${price}</span>
                      </div>
                    ` : `
                      <div class="price__regular">
                        <span class="price-item price-item--regular">¥${price}</span>
                      </div>
                    `}
                  </div>
                </div>
                ${cartHtml}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    return li;
  }

  // カラーバリエーションのスウォッチを生成
  buildColorSwatches(product) {
    if (!product.options || !product.variants) return '';
    let colorIndex = -1;
    product.options.forEach((opt, i) => {
      const name = (typeof opt === 'string' ? opt : (opt.name || '')).toLowerCase();
      if (name === 'color' || name === 'colour' || name === 'カラー' || name === '色') {
        colorIndex = i;
      }
    });
    if (colorIndex === -1) return '';

    // カラー値の一覧（重複排除・出現順）
    const seen = new Set();
    const values = [];
    product.variants.forEach((v) => {
      const val = v.options ? v.options[colorIndex] : null;
      if (val && !seen.has(val)) {
        seen.add(val);
        values.push({
          value: val,
          variantId: v.id,
          available: v.available,
          image: v.featured_image ? v.featured_image.src : '',
        });
      }
    });
    if (values.length <= 1) return '';

    const items = values.map((c, idx) => {
      const bg = c.image
        ? `background-image:url(${c.image}&width=48);`
        : `background-color:${this.colorToHex(c.value)};`;
      return `<li class="card__color-swatch-item"><button type="button" class="card__color-swatch-link${idx === 0 ? ' is-selected' : ''}" data-variant-id="${c.variantId}" data-image="${c.image || ''}" title="${c.value}" aria-label="${c.value}"><span class="swatch" style="${bg}"></span></button></li>`;
    }).join('');

    return `<ul class="card__color-swatches list-unstyled" role="list">${items}</ul>`;
  }

  // カラー名→色（バリアント画像が無い場合のフォールバック）
  colorToHex(name) {
    const map = {
      'ブラック': '#1a1a1a', '黒': '#1a1a1a', 'ホワイト': '#ffffff', '白': '#ffffff',
      'ネイビー': '#1f2a44', 'グレー': '#888888', 'グレージュ': '#b8aa97', 'ベージュ': '#d8c4a5',
      'アイボリー': '#f3ecdd', 'ブラウン': '#5a3d2b', '茶': '#5a3d2b', 'キャメル': '#c19a6b',
      'グリーン': '#3a5a40', 'カーキ': '#6b6b4b', 'レッド': '#a8332e', '赤': '#a8332e',
      'ブルー': '#33557a', '青': '#33557a', 'イエロー': '#e0b000', 'ピンク': '#d98a9a',
    };
    return map[String(name).trim()] || '#cccccc';
  }

  // カートに追加するボタンを生成（常に「カートに追加する」・遷移しない）
  buildCartButton(product) {
    if (!product.variants || product.variants.length === 0) return '';
    const firstAvail = product.variants.find((v) => v.available) || product.variants[0];
    if (!firstAvail.available) {
      return `<div class="quick-add no-js-hidden"><button type="button" class="button button--full-width button--secondary" disabled>売り切れ</button></div>`;
    }
    return `<div class="quick-add no-js-hidden"><button type="button" class="button button--full-width button--secondary tsog-rv__cart-btn" data-variant-id="${firstAvail.id}">カートに追加する</button></div>`;
  }

  // カード内のスウォッチ選択・カート追加をバインド
  bindCartButtons(listElement) {
    listElement.querySelectorAll('.tsog-recently-viewed__item').forEach((card) => {
      const cartBtn = card.querySelector('.tsog-rv__cart-btn');
      const mainImg = card.querySelector('.card__media img');

      // スウォッチ選択 → カート対象バリアントと画像を切り替え（遷移しない）
      card.querySelectorAll('.card__color-swatch-link[data-variant-id]').forEach((sw) => {
        sw.addEventListener('click', (e) => {
          e.preventDefault();
          card.querySelectorAll('.card__color-swatch-link').forEach((s) => s.classList.remove('is-selected'));
          sw.classList.add('is-selected');
          if (cartBtn) cartBtn.dataset.variantId = sw.dataset.variantId;
          const img = sw.dataset.image;
          if (mainImg && img) mainImg.src = img + '&width=533';
        });
      });

      // カートに追加（その場で追加・カートへ）
      if (cartBtn) {
        cartBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          const id = cartBtn.dataset.variantId;
          if (!id) return;
          const original = cartBtn.textContent;
          cartBtn.disabled = true;
          cartBtn.textContent = '追加中...';
          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
            });
            if (res.ok) {
              window.location.href = '/cart';
            } else {
              cartBtn.disabled = false;
              cartBtn.textContent = original;
            }
          } catch (err) {
            cartBtn.disabled = false;
            cartBtn.textContent = original;
          }
        });
      }
    });
  }

  // Splideスライダーを初期化
  initializeSlider(sectionId, perPage, totalProducts, perPageMobile = 2) {
    const sliderElement = document.querySelector(`.tsog-recently-viewed-splide-${sectionId}`);

    if (!sliderElement || sliderElement.splide) {
      return;
    }

    const splideConfig = {
      type: 'slide',
      perPage: perPage,
      perMove: 1,
      gap: '2rem',
      padding: 0,
      arrows: false,
      pagination: false,
      drag: true,
      keyboard: true,
      autoWidth: false,
      focus: 0,
      trimSpace: false,

      breakpoints: {
        640: {
          perPage: perPageMobile,
          gap: '1rem',
        },
        990: {
          perPage: Math.min(perPage, 2),
          gap: '1.5rem',
        },
        1200: {
          perPage: Math.min(perPage, 3),
          gap: '2rem',
        },
      },

      i18n: {
        prev: '前のスライド',
        next: '次のスライド',
        first: '最初のスライドへ',
        last: '最後のスライドへ',
        slideX: 'スライド %s',
        pageX: 'ページ %s',
      },
    };

    const progressBar = document.querySelector(`.tsog-recently-viewed-progress__bar[data-section-id="${sectionId}"]`);
    const progressFill = progressBar ? progressBar.querySelector('.tsog-recently-viewed-progress__fill') : null;

    try {
      const splide = new Splide(sliderElement, splideConfig);

      const updateProgressBar = () => {
        if (!progressFill) return;

        const totalSlides = splide.length;
        const currentIndex = splide.index;

        const progress = totalSlides > 1 ? ((currentIndex) / (totalSlides - 1)) * 100 : 0;
        progressFill.style.width = `${Math.min(progress, 100)}%`;
      };

      splide.on('mounted', () => {
        updateProgressBar();
      });

      splide.on('moved', (newIndex) => {
        updateProgressBar();

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'slide_change', {
            event_category: 'Recently Viewed',
            event_label: sectionId,
            value: newIndex
          });
        }
      });

      splide.mount();
    } catch (error) {
      console.error('Failed to initialize Splide for recently viewed:', error);
    }
  }
}

// Shopifyテーマエディターでのリロード対応
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    setTimeout(() => {
      new TsogRecentlyViewed();
    }, 100);
  });

  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(() => {
      new TsogRecentlyViewed();
    }, 100);
  });
}

// 初期化実行
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TsogRecentlyViewed();
  });
} else {
  new TsogRecentlyViewed();
}
